import React, {
  useState,
  useEffect,
  useRef,
  useContext,
  createContext
} from "react";
import PropTypes from "prop-types";
import * as d3 from "d3";
import { formatCurrencyShort } from "@wrstudios/utils";
import { Container } from "./styled/scatter-plot";

const ScatterPlotContext = createContext();
const useGraph = () => useContext(ScatterPlotContext);

function getTicks(min, max, numMiddleTicks) {
  const delta = (max - min) / (numMiddleTicks + 1);
  const middleTicks = new Array(numMiddleTicks)
    .fill()
    .map((_, i) => min + (i + 1) * delta);
  return [min, ...middleTicks, max];
}

function ScatterPlot({
  width,
  height,
  widthOffset,
  heightOffset,
  minX,
  maxX,
  minY,
  maxY,
  xLabel,
  children,
  ...props
}) {
  const idRef = useRef(Math.random().toString(16).substr(2));
  const svgRef = useRef();
  const xRef = useRef();
  const yRef = useRef();
  const containerRef = useRef();
  const [, forceUpdate] = useState();
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    svgRef.current = d3
      .select(containerRef.current)
      .append("svg")
      .attr("height", height + (xLabel ? heightOffset / 2 : 0));

    const clipPath = svgRef.current
      .append("clipPath")
      .attr("id", `clip-path-${idRef.current}`)
      .append("rect");

    // X Axis
    xRef.current = d3.scaleLinear().domain([minX, maxX]);
    const xAxis = d3
      .axisBottom(xRef.current)
      .tickValues(getTicks(minX, maxX, 2));
    const xAxisNode = svgRef.current.append("g").attr("class", "x-axis");

    let label;
    if (xLabel) {
      label = svgRef.current
        .append("text")
        .style("text-anchor", "middle")
        .attr("class", "x-label")
        .text(xLabel);
    }

    // Y Axis
    yRef.current = d3.scaleLinear().domain([minY, maxY]);
    const yAxis = d3
      .axisRight(yRef.current)
      .tickValues(getTicks(minY, maxY, 2))
      .tickFormat((d) => formatCurrencyShort(d));
    const yAxisNode = svgRef.current.append("g").attr("class", "y-axis");

    // Grid Lines
    const gridAxis = d3
      .axisLeft(yRef.current)
      .tickValues(getTicks(minY, maxY, 2))
      .tickSize(-(width - widthOffset))
      .tickFormat("")
      .tickSizeOuter(1);
    const gridAxisNode = svgRef.current.append("g").attr("class", "grid");

    const resize = () => {
      if (!containerRef.current) return;

      const xPadding = 40;
      const { width, height } = containerRef.current.getBoundingClientRect();

      d3.selectAll(`#scatter-plot-${idRef.current} svg`).attr("width", width);

      clipPath
        .attr("x", 0)
        .attr("y", 15)
        .attr("width", width - widthOffset)
        .attr("height", [height - heightOffset - 15]);

      xRef.current.range([xPadding, width - widthOffset - xPadding]);
      yRef.current.range([height - heightOffset, 15]);
      gridAxis.tickSize(-(width - widthOffset));

      xAxisNode
        .attr("transform", `translate(0, ${height - heightOffset + 5})`)
        .call(xAxis);
      yAxisNode
        .attr("transform", `translate(${width - widthOffset}, 0)`)
        .call(yAxis);
      gridAxisNode.call(gridAxis);

      if (xLabel) {
        label.attr(
          "transform",
          `translate(${(width - widthOffset) / 2}, ${height - 5})`
        );
      }

      forceUpdate({});
      setIsLoaded(true);
    };

    d3.select(window).on("resize", resize);
    resize();

    return () => {
      d3.selectAll(`#scatter-plot-${idRef.current} svg`).remove();
    };
  }, [width, height, widthOffset, heightOffset, minX, maxX, minY, maxY]);

  return (
    <ScatterPlotContext.Provider
      value={{
        id: idRef.current,
        svg: svgRef.current,
        plotX: xRef.current,
        plotY: yRef.current
      }}>
      {isLoaded && <div id="loaded" />}
      <Container
        id={`scatter-plot-${idRef.current}`}
        ref={containerRef}
        {...props}>
        {svgRef.current ? children : null}
      </Container>
    </ScatterPlotContext.Provider>
  );
}

ScatterPlot.defaultProps = {
  width: 960,
  height: 500,
  widthOffset: 75,
  heightOffset: 58
};

ScatterPlot.propTypes = {
  width: PropTypes.number,
  height: PropTypes.number,
  widthOffset: PropTypes.number,
  heightOffset: PropTypes.number,
  minX: PropTypes.number.isRequired,
  maxX: PropTypes.number.isRequired,
  minY: PropTypes.number.isRequired,
  maxY: PropTypes.number.isRequired,
  xLabel: PropTypes.string
};

function Dot({
  x,
  y,
  radius,
  borderWidth,
  outlineColor,
  color,
  onClick,
  hasLabel,
  label,
  labelColor,
  labelSize
}) {
  const { svg, plotX, plotY } = useGraph();
  const hasOnClick = typeof onClick === "function";

  useEffect(() => {
    const dotWrapper = svg.append("g");

    // Outline
    dotWrapper
      .append("circle")
      .attr("cx", plotX(x))
      .attr("cy", plotY(y))
      .attr("r", radius)
      .style("fill", outlineColor)
      .style("cursor", hasOnClick ? "pointer" : "default")
      .on("click", () => (hasOnClick ? onClick(d3.event) : {}));

    // Circle
    dotWrapper
      .append("circle")
      .attr("cx", plotX(x))
      .attr("cy", plotY(y))
      .attr("r", radius - borderWidth)
      .style("fill", color)
      .style("cursor", hasOnClick ? "pointer" : "default")
      .on("click", () => (hasOnClick ? onClick(d3.event) : {}));

    // Label
    {
      hasLabel &&
        dotWrapper
          .append("text")
          .attr("x", plotX(x))
          .attr("y", plotY(y))
          .attr("text-anchor", "middle")
          .attr("alignment-baseline", "middle")
          .style("fill", labelColor)
          .style("font-size", labelSize)
          .style("font-weight", "400")
          .text(label);
    }

    return () => {
      dotWrapper.remove();
    };
  });

  return null;
}

Dot.defaultProps = {
  radius: 8,
  borderWidth: 1.5,
  outlineColor: "#000",
  color: "#fff",
  hasLabel: false,
  label: "",
  labelColor: "#fff",
  labelSize: "0.5em"
};

Dot.propTypes = {
  x: PropTypes.number.isRequired,
  y: PropTypes.number.isRequired,
  radius: PropTypes.number.isRequired,
  borderWidth: PropTypes.number.isRequired,
  color: PropTypes.string,
  outline: PropTypes.bool,
  onClick: PropTypes.func,
  hasLabel: PropTypes.bool,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  labelColor: PropTypes.string,
  labelSize: PropTypes.string
};

function Line({ x1, y1, x2, y2, width, color, dash }) {
  const { id, svg, plotX, plotY } = useGraph();

  useEffect(() => {
    const line = svg
      .append("line")
      .attr("clip-path", `url(#clip-path-${id})`)
      .attr("class", "line")
      .attr("x1", plotX(x1))
      .attr("y1", plotY(y1))
      .attr("x2", plotX(x2))
      .attr("y2", plotY(y2))
      .attr("stroke", color)
      .attr("stroke-width", width)
      .style("stroke-dasharray", dash);

    return () => {
      line.remove();
    };
  });

  return null;
}

Line.defaultProps = {
  width: 1,
  color: "#000",
  dash: "0"
};

Line.propTypes = {
  x1: PropTypes.number.isRequired,
  y1: PropTypes.number.isRequired,
  x2: PropTypes.number.isRequired,
  y2: PropTypes.number.isRequired,
  width: PropTypes.number,
  color: PropTypes.string,
  dash: PropTypes.string
};

export function DynamicLine({ data, width, color, dash }) {
  const { id, svg, plotX, plotY } = useGraph();

  useEffect(() => {
    const d3Line = d3
      .line()
      .x(([x]) => plotX(x))
      .y(([, y]) => plotY(y));
    const line = svg
      .append("path")
      .attr("d", d3Line(data))
      .attr("clip-path", `url(#clip-path-${id})`)
      .attr("class", "line dynamic-line")
      .attr("stroke", color)
      .attr("stroke-width", width)
      .style("stroke-dasharray", dash);

    return () => {
      line.remove();
    };
  });

  return null;
}

DynamicLine.defaultProps = {
  width: 1,
  color: "#000",
  dash: "0"
};

DynamicLine.propTypes = {
  data: PropTypes.arrayOf(PropTypes.array).isRequired,
  width: PropTypes.number,
  color: PropTypes.string,
  dash: PropTypes.string
};

function Area({ line1, line2, color }) {
  const { id, svg, plotX, plotY } = useGraph();

  useEffect(() => {
    const data = [
      {
        x1: plotX(line1.x1),
        y1: plotY(line1.y1),
        x2: plotX(line1.x2),
        y2: plotY(line1.y2)
      },
      {
        x1: plotX(line2.x1),
        y1: plotY(line2.y1),
        x2: plotX(line2.x2),
        y2: plotY(line2.y2)
      }
    ];
    const area = d3
      .area()
      .x0((d) => d.x1)
      .x1((d) => d.x2)
      .y0((d) => d.y1)
      .y1((d) => d.y2);

    const svgArea = svg
      .append("path")
      .datum(data)
      .attr("clip-path", `url(#clip-path-${id})`)
      .attr("fill", color)
      .attr("d", area);

    return () => {
      svgArea.remove();
    };
  });

  return null;
}

Area.defaultProps = {
  color: "#000"
};

Area.propTypes = {
  line1: PropTypes.shape({
    x1: PropTypes.number.isRequired,
    y1: PropTypes.number.isRequired,
    x2: PropTypes.number.isRequired,
    y2: PropTypes.number.isRequired
  }).isRequired,
  line2: PropTypes.shape({
    x1: PropTypes.number.isRequired,
    y1: PropTypes.number.isRequired,
    x2: PropTypes.number.isRequired,
    y2: PropTypes.number.isRequired
  }).isRequired,
  color: PropTypes.string
};

function SVG({ width, height, x, y, paths, color, transform, onClick }) {
  const { svg, plotX, plotY } = useGraph();

  useEffect(() => {
    const group = svg
      .append("g")
      .attr(
        "transform",
        transform || `translate(-${width / 2}, -${height / 2})`
      )
      .append("svg")
      .attr("width", width)
      .attr("height", height)
      .attr("x", plotX(x))
      .attr("y", plotY(y))
      .on("click", onClick);

    paths.forEach((path) =>
      group.append("path").attr("d", path).attr("fill", color)
    );

    return () => {
      group.remove();
    };
  });

  return null;
}

SVG.defaultProps = {
  color: "#000"
};

SVG.propTypes = {
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
  x: PropTypes.number.isRequired,
  y: PropTypes.number.isRequired,
  paths: PropTypes.arrayOf(PropTypes.string).isRequired,
  color: PropTypes.string,
  transform: PropTypes.string,
  onClick: PropTypes.func
};

export { ScatterPlot, Dot, Line, Area, SVG };
